import { Anchor, Badge, Button, Checkbox, Group, Stack, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EmptyNote } from '../../components/RecordScreen';
import { notifyDone, notifyError } from '../../lib/notify';
import { roleMembersPath } from './permissions';

/**
 * Who does this job, on the page that says what the job allows.
 *
 * The role screen could answer "what may a Shift Lead do at the register" in fifty lines of detail and
 * could not answer "who is a Shift Lead" at all — that was only knowable by opening every member of
 * staff in turn and reading their badges. Its own alert used to send the reader away to give the
 * role to somebody: *open a person under People to give it to them*. That sentence is the smell,
 * and this is what replaces it.
 *
 * Deliberately NOT the person-side write. That one replaces everything a person holds, so calling
 * it from here would need this screen to know every OTHER role each person has — and getting that
 * wrong strips a bartender of the kitchen role nobody on this screen was thinking about. The
 * endpoint behind this one touches this role and nothing else.
 */

interface RoleMember {
  staffId: string;
  displayName: string;
  status: string;
}

interface StaffMember {
  id: string;
  displayName: string;
  status: string;
}

const MEMBERS_PATH = '/admin/v1/staff/members';

export function RoleHoldersPanel({ roleId, roleName }: { roleId: string; roleName: string }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  const holders = useQuery({
    queryKey: listKey(roleMembersPath(roleId)),
    queryFn: () => rawApi<RoleMember[]>('GET', roleMembersPath(roleId)),
    enabled: Boolean(roleId),
  });

  const everyone = useQuery({
    queryKey: listKey(MEMBERS_PATH),
    queryFn: () => rawApi<StaffMember[]>('GET', MEMBERS_PATH),
    enabled: editing,
  });

  const save = useMutation({
    mutationFn: () => rawApi('PUT', roleMembersPath(roleId), { staffIds: selected }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listKey(roleMembersPath(roleId)) });
      /*
       * Everybody whose roles changed now does something different at the register, and their own page
       * says so in a sentence the API works out. Matched by suffix rather than by id because it is
       * every person's answer that may have gone stale, not one we happen to know the id of.
       */
      void queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[1] === 'string' && query.queryKey[1].endsWith('/capabilities'),
      });
      setEditing(false);
      notifyDone(
        'Roles updated',
        selected.length === 0
          ? `Nobody holds ${roleName} now, so nothing it allows reaches a register.`
          : `${selected.length === 1 ? '1 person' : `${selected.length} people`} now hold ${roleName}.`,
      );
    },
    onError: (err) => notifyError(err, `change who does ${roleName}`),
  });

  const held = holders.data ?? [];

  if (holders.isLoading) return <EmptyNote>Loading…</EmptyNote>;
  if (holders.isError && !holders.data) {
    return <EmptyNote>Who holds this role could not be read. Refresh the page to try again.</EmptyNote>;
  }

  if (editing) {
    const roster = everyone.data ?? [];
    // A leaver keeps whatever roles were on their record — nothing takes one away when employment
    // ends — so they have to be tickable here, or somebody who left in March could never be taken
    // off the list of people allowed to approve a comp.
    const stillHere = roster.filter((s) => s.status !== 'terminated' || selected.includes(s.id));
    return (
      <Stack gap="md">
        {everyone.isLoading ? (
          // Still arriving is not the same as "there is nobody". This opens the instant the button
          // is pressed, usually before the list has come back.
          <EmptyNote>Loading…</EmptyNote>
        ) : everyone.isError ? (
          <EmptyNote>The list of staff could not be loaded. Try again in a moment.</EmptyNote>
        ) : stillHere.length === 0 ? (
          <EmptyNote>Nobody has been added under People yet.</EmptyNote>
        ) : (
          <>
            <Text size="sm" c="dimmed">
              Tick everybody who does this job. Unticking somebody takes away what {roleName} allowed them —
              anything their other roles allow is untouched.
            </Text>
            <Checkbox.Group value={selected} onChange={setSelected}>
              <Stack gap="sm">
                {stillHere.map((person) => (
                  <Checkbox
                    key={person.id}
                    value={person.id}
                    label={person.displayName}
                    description={person.status === 'terminated' ? 'No longer works here' : undefined}
                    disabled={save.isPending}
                  />
                ))}
              </Stack>
            </Checkbox.Group>
          </>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setEditing(false)} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={everyone.isLoading || everyone.isError}
          >
            Save
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div style={{ minWidth: 0 }}>
          {held.length === 0 ? (
            <EmptyNote>Nobody does this job yet, so nothing it allows reaches a register.</EmptyNote>
          ) : (
            <Group gap="xs">
              {held.map((person) => (
                // A name is a link because the next question is always "and what else does she
                // do?" — which is her page, not a tooltip.
                <Anchor key={person.staffId} component={Link} to={`/staff/${person.staffId}`}>
                  <Badge variant="light" size="lg" style={{ cursor: 'pointer' }}>
                    {person.displayName}
                  </Badge>
                </Anchor>
              ))}
            </Group>
          )}
        </div>
        <Button
          size="sm"
          variant="light"
          onClick={() => {
            setSelected(held.map((p) => p.staffId));
            setEditing(true);
          }}
        >
          {held.length === 0 ? 'Give it to somebody' : 'Change who does it'}
        </Button>
      </Group>
      {/* Only where somebody on the list has left. Their role is still on their record, which is
          the fact that can actually be acted on from here. */}
      {held.some((p) => p.status === 'terminated') && (
        <Text size="sm" c="orange">
          Somebody on this list no longer works here. Their role is still on their record — take it away if it
          should no longer count.
        </Text>
      )}
    </Stack>
  );
}
